/**
 * HeroBackdrop — cinematic hero background plate.
 *
 * Renders cover image + tint + scrims only. The dust particles are drawn by
 * <SectionParticles /> mounted alongside in each Hero variant, so a single
 * canvas system handles every section consistently.
 */

import React from 'react';
import { useReducedMotion } from 'framer-motion';

export default function HeroBackdrop({ coverUrl, name, fullBleed = false }) {
    const reduce = useReducedMotion();

    return (
        <div
            className={[
                'relative w-full h-full overflow-hidden',
                fullBleed
                    ? ''
                    : 'rounded-[2rem] border border-[var(--tenant-border)] bg-[var(--tenant-surface-alt)]'
            ].join(' ')}
        >
            {coverUrl && (
                <div
                    className="absolute inset-0 will-change-transform"
                    style={{
                        backgroundImage: `url(${coverUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: fullBleed ? 0.32 : 0.55,
                        filter: fullBleed ? 'saturate(0.9) contrast(1.02)' : 'saturate(1.05) contrast(1.02)',
                        animation: reduce ? 'none' : 'tenantHeroDrift 28s ease-in-out infinite alternate'
                    }}
                />
            )}

            <div
                aria-hidden
                className="absolute inset-0"
                style={{
                    background: fullBleed
                        ? 'radial-gradient(120% 90% at 15% 25%, var(--tenant-primary) 0%, var(--tenant-primary-soft) 35%, transparent 75%)'
                        : 'radial-gradient(140% 90% at 30% 20%, var(--tenant-primary-soft), transparent 60%), linear-gradient(180deg, transparent 0%, var(--tenant-surface) 100%)',
                    opacity: fullBleed ? 0.55 : 1,
                    mixBlendMode: fullBleed ? 'multiply' : 'normal'
                }}
            />

            {fullBleed && (
                <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                        background:
                            'linear-gradient(90deg, var(--tenant-ink) 0%, var(--tenant-ink) 8%, transparent 55%)',
                        opacity: 0.18
                    }}
                />
            )}

            {fullBleed && (
                <div
                    aria-hidden
                    className="absolute inset-x-0 bottom-0 h-1/3"
                    style={{
                        background:
                            'linear-gradient(180deg, transparent 0%, var(--tenant-surface-alt) 100%)',
                        opacity: 0.55
                    }}
                />
            )}

            <div
                aria-hidden
                className="absolute inset-0"
                style={{
                    boxShadow: fullBleed
                        ? 'inset 0 0 220px 60px var(--tenant-surface)'
                        : 'inset 0 0 120px 30px var(--tenant-surface)'
                }}
            />

            {!fullBleed && (
                <>
                    <span aria-hidden className="absolute top-5 left-5 w-6 h-6 border-l border-t border-[var(--tenant-ink)]/30" />
                    <span aria-hidden className="absolute top-5 right-5 w-6 h-6 border-r border-t border-[var(--tenant-ink)]/30" />
                    <span aria-hidden className="absolute bottom-5 left-5 w-6 h-6 border-l border-b border-[var(--tenant-ink)]/30" />
                    <span aria-hidden className="absolute bottom-5 right-5 w-6 h-6 border-r border-b border-[var(--tenant-ink)]/30" />
                    {name && (
                        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-3 pointer-events-none">
                            <div className="font-headline font-extrabold tracking-[-0.02em] text-2xl text-[var(--tenant-ink)] leading-none">
                                {name}
                            </div>
                            <div className="text-[9px] uppercase tracking-[0.32em] text-[var(--tenant-ink)]/50">
                                Est. workshop
                            </div>
                        </div>
                    )}
                </>
            )}

            <style>{`
                @keyframes tenantHeroDrift {
                    0%   { transform: scale(1.04) translate(0, 0); }
                    50%  { transform: scale(1.08) translate(-1.2%, -1%); }
                    100% { transform: scale(1.05) translate(0.8%, 0.6%); }
                }
            `}</style>
        </div>
    );
}
